<script setup>
import {Head, Link, usePage} from '@inertiajs/vue3';
import {BRAND_NAME} from "../constants.js";
import {ref, computed, watch} from "vue";
import ApplicationLogo from "@/Components/ApplicationLogo.vue";

// Define props first
const props = defineProps({
    userSegment: {
        type: String,
        default: null
    },
    showDefaultPromo: {
        type: Boolean,
        default: true
    }
});


// Get page data using usePage
const page = usePage();
console.log(page.props)
// Mobile menu state
const isMobileMenuOpen = ref(false);
const isResourcesMenuMobileOpen = ref(false);
const openSubMenuDropdownIndex = ref(null);

// User segment handling with proper initialization
const userSegment = ref(props.userSegment || 'ielts');

const headerOffer = computed(() => page.props.header_offer || null);

// Format the end date for display
const formatEndDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {month: 'long', day: 'numeric'});
};

const proPlanEndingDate = function () {
    const date = new Date();
    date.setDate(date.getDate() + 2);
    return date.toLocaleDateString('en-US', {month: 'long', day: 'numeric'})
}

// Initialize from localStorage if available (after defining the ref)
if (typeof window !== 'undefined') {
    const storedSegment = localStorage.getItem('userSegment');
    if (storedSegment) {
        userSegment.value = storedSegment;
    }
}

// Watch for changes in props.userSegment
watch(() => props.userSegment, (newVal) => {
    if (newVal) {
        userSegment.value = newVal;
        if (typeof window !== 'undefined') {
            localStorage.setItem('userSegment', newVal);
        }
    }
}, {immediate: true});

// Toggle functions
const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const toggleSubMenuDropdown = (index) => {
    if (openSubMenuDropdownIndex.value === index) {
        openSubMenuDropdownIndex.value = null;
    } else {
        openSubMenuDropdownIndex.value = index;
    }
};


// Computed properties for SEO using usePage
const pageTitle = computed(() => page.props.title || 'Practice English Speaking Online | MockReady');
const pageDescription = computed(() => page.props.description || 'Improve your English speaking skills with AI-powered practice and real human conversations. Perfect for IELTS preparation or general fluency.');
const pageUrl = computed(() => page.props.url || 'https://mockready.com/');

// Expose userSegment to parent components
defineExpose({userSegment});
</script>

<template>
    <!-- Only show the promo banner when there's an offer from the backend -->
    <div v-if="headerOffer"
         class="flex text-center justify-center items-center p-4 text-sm text-white bg-gradient-to-r from-blue-600 to-blue-800 dark:bg-gray-800 dark:text-blue-400"
         role="alert">
        <svg class="flex-shrink-0 inline w-4 h-4 me-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
             fill="currentColor" viewBox="0 0 20 20">
            <path
                d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"/>
        </svg>
        <div class="flex flex-wrap justify-center items-center">
            <span class="font-medium mr-2">Limited Time Offer:</span>
            <span>{{
                    headerOffer.percentage
                }}% off your first month when you sign up today. Offer ends {{
                    formatEndDate(headerOffer.end_date)
                }}!</span>
            <a :href="route('partner.offer', {promo: headerOffer.code})"
               class="ml-3 bg-white text-blue-700 px-3 py-1 rounded-full text-xs font-bold hover:bg-blue-50 transition">
                Claim Offer
            </a>
        </div>
    </div>
    <header>
        <nav class="bg-white border-gray-200 py-2.5 dark:bg-gray-800 border-b dark:border-gray-700">
            <div class="flex flex-wrap justify-between items-center mx-auto max-w-screen-xl">
                <Link :href="route('home')" class="flex items-center">
                    <ApplicationLogo variant="primary" class="h-8 w-auto sm:h-10"/>
                </Link>
                <div class="flex items-center lg:order-2">
                    <Link v-if="!page.props.is_authenticated" :href="route('login')"
                          class="text-gray-800 dark:text-white hover:bg-gray-50 focus:ring-4 focus:ring-gray-300 font-medium rounded-md text-sm px-3 lg:px-5 py-2 lg:py-2.5 mr-2 dark:hover:bg-gray-700 focus:outline-none dark:focus:ring-gray-800">
                        Log
                        in
                    </Link>
                    <Link v-if="!page.props.is_authenticated" :href="route('register')"
                          class="text-white bg-primary-500 hover:bg-primary-600 focus:ring-4 focus:ring-primary-300 font-medium rounded-md text-sm px-3 lg:px-5 py-2 lg:py-2.5 mr-2 dark:bg-primary-400 dark:hover:bg-primary-700 focus:outline-none dark:focus:ring-primary-800">
                        Sign Up
                    </Link>


                    <Link v-if="page.props.is_authenticated" :href="route('register')"
                          class="text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 font-medium rounded-md text-sm px-3 lg:px-5 py-2 lg:py-2.5 mr-2 dark:bg-primary-600 dark:hover:bg-primary-700 focus:outline-none dark:focus:ring-primary-800">
                        Dashboard
                    </Link>

                    <div class="-mr-2 flex items-center sm:hidden">
                        <button
                            @click="isMobileMenuOpen = !isMobileMenuOpen"
                            class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-900 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-900 focus:text-gray-500 dark:focus:text-gray-400 transition duration-150 ease-in-out"
                            :aria-expanded="isMobileMenuOpen ? 'true' : 'false'"
                        >
                            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    :class="{
                                            hidden: isMobileMenuOpen,
                                            'inline-flex': !isMobileMenuOpen,
                                        }"
                                    d="M4 6h16M4 12h16M4 18h16"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                />
                                <path
                                    :class="{
                                            hidden: !isMobileMenuOpen,
                                            'inline-flex': isMobileMenuOpen,
                                        }"
                                    d="M6 18L18 6M6 6l12 12"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
                <div :class="{ 'block': isMobileMenuOpen, 'hidden': !isMobileMenuOpen }"
                     class="justify-between items-center w-full lg:flex lg:w-auto lg:order-1" id="mobile-menu-2">
                    <ul class="flex flex-col md:items-center mt-4 font-medium lg:flex-row lg:space-x-8 lg:mt-0">
                        <li>
                            <Link :href="route('home')"
                                  class="block py-2 pr-4 pl-3 text-white rounded bg-primary-700 lg:bg-transparent lg:text-primary-700 lg:p-0 dark:text-white dark:lg:text-white"
                                  aria-current="page">Home
                            </Link>
                        </li>
                        <li>
                            <a href="/blog/"
                               class="block py-2 pr-4 pl-3 text-gray-700 border-b border-gray-100 hover:bg-gray-50 lg:hover:bg-transparent lg:border-0 lg:hover:text-primary-700 lg:p-0 dark:text-gray-400 lg:dark:hover:text-white dark:hover:bg-gray-700 dark:hover:text-white lg:dark:hover:bg-transparent dark:border-gray-700"
                               aria-label="English learning tips and resources">Blog
                            </a>
                        </li>
                        <li>
                            <Link :href="route('page.about_us')"
                                  class="block py-2 pr-4 pl-3 text-gray-700 border-b border-gray-100 hover:bg-gray-50 lg:hover:bg-transparent lg:border-0 lg:hover:text-primary-700 lg:p-0 dark:text-gray-400 lg:dark:hover:text-white dark:hover:bg-gray-700 dark:hover:text-white lg:dark:hover:bg-transparent dark:border-gray-700">
                                About
                                us
                            </Link>
                        </li>
                        <li>
                            <Link :href="route('page.faq')"
                                  class="block py-2 pr-4 pl-3 text-gray-700 border-b border-gray-100 hover:bg-gray-50 lg:hover:bg-transparent lg:border-0 lg:hover:text-primary-700 lg:p-0 dark:text-gray-400 lg:dark:hover:text-white dark:hover:bg-gray-700 dark:hover:text-white lg:dark:hover:bg-transparent dark:border-gray-700">
                                FAQ
                            </Link>
                        </li>
                        <li>
                            <Link :href="route('page.contact_us')"
                                  class="block py-2 pr-4 pl-3 text-gray-700 border-b border-gray-100 hover:bg-gray-50 lg:hover:bg-transparent lg:border-0 lg:hover:text-primary-700 lg:p-0 dark:text-gray-400 lg:dark:hover:text-white dark:hover:bg-gray-700 dark:hover:text-white lg:dark:hover:bg-transparent dark:border-gray-700">
                                Contact
                            </Link>
                        </li>
                    </ul>
                </div>


            </div>
        </nav>
    </header>
    <header v-if="$slots.header" class="text-center mx-auto bg-white dark:bg-gray-900">
        <div class="py-6">
            <slot name="header"/>
        </div>
    </header>
    <div class="mx-auto bg-white dark:bg-gray-900">
        <section class="">
            <div class="mx-auto">
                <slot></slot>
            </div>
        </section>

    </div>

    <section class="bg-primary-500 py-12 text-white">
        <div class="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <!-- Dynamic headline based on user segment -->
            <h2 class="text-3xl font-bold mb-4">
                {{
                    userSegment === 'ielts' ? 'Ready to Ace Your IELTS Speaking Test?' : 'Ready to Speak English with Confidence?'
                }}
            </h2>

            <!-- Dynamic subtext based on user segment -->
            <p class="text-lg mb-8 max-w-2xl mx-auto">
                {{
                    userSegment === 'ielts'
                        ? 'Join thousands of test-takers who have boosted their IELTS scores with MockReady'
                        : 'Join thousands of learners who have improved their English speaking fluency with MockReady'
                }}
            </p>

            <!-- Dynamic CTA based on user segment -->
            <div class="max-w-md mx-auto">
                <a href="/register"
                   class="block w-full bg-white text-primary-600 font-bold py-4 px-8 rounded-lg text-lg hover:bg-gray-100 transition duration-200 mb-4">
                    {{ userSegment === 'ielts' ? 'Start IELTS Practice Free' : 'Start English Practice Free' }}
                </a>
                <p class="text-sm">No credit card required. Try it risk-free today.</p>
            </div>
        </div>
    </section>
    <footer class="p-4 bg-[#00200D] text-white sm:p-6 dark:bg-gray-800">
        <div class="mx-auto max-w-screen-xl">
            <div class="md:flex md:justify-between">
                <div class="mb-6 mr-8 md:mb-0">
                    <Link :href="route('home')"
                          class="flex justify-start items-center text-2xl font-semibold text-white dark:text-white">
                        <ApplicationLogo class="" variant="white"/>
                    </Link>
                    <div class="pt-8">
                        <div class="flex mt-4 space-x-6 sm:justify-center sm:mt-0">
                            <a href="https://www.facebook.com/mockready" target="_blank"
                               class="text-gray-100 hover:text-gray-100 dark:hover:text-white">
                                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path fill-rule="evenodd"
                                          d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                                          clip-rule="evenodd"/>
                                </svg>
                            </a>
                            <a href="https://www.instagram.com/mockready/" target="_blank"
                               class="text-gray-100 hover:text-gray-100 dark:hover:text-white">
                                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path fill-rule="evenodd"
                                          d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                                          clip-rule="evenodd"/>
                                </svg>
                            </a>
                            <a href="https://twitter.com/mockready" target="_blank"
                               class="text-gray-100 hover:text-gray-100 dark:hover:text-white">
                                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path
                                        d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"/>
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
                <div class="grid grid-cols-2 gap-2 sm:gap-2 sm:grid-cols-3">
                    <div>
                        <h2 class="mb-6 text-md font-semibold text-white uppercase dark:text-white">Resources</h2>
                        <ul class="text-white dark:text-gray-400">
                            <li class="mb-4">
                                <Link :href="route('page.faq')" class="mr-4 hover:underline md:mr-6">Home</Link>
                            </li>
                            <li class="mb-4">
                                <Link :href="route('page.about_us')" class="mr-4 hover:underline md:mr-6">About</Link>
                            </li>
                            <li class="mb-4">
                                <a href="/blog/" class="mr-4 hover:underline md:mr-6 ">Blog &
                                    News
                                </a>
                            </li>
                            <li class="mb-4">
                                <Link :href="route('page.faq')" class="mr-4 hover:underline md:mr-6">FAQs</Link>
                            </li>

                        </ul>
                    </div>
                    <div>
                        <h2 class="mb-6 text-md font-semibold text-white uppercase dark:text-white">Learning
                            English</h2>
                        <ul class="text-white dark:text-gray-400">
                            <ul class="text-white dark:text-gray-400">
                                <li class="mb-4">
                                    <Link :href="route('content_hubs.speaking_exam_material')"
                                          class="mr-4 hover:underline md:mr-6">IELTS Speaking Material
                                    </Link>
                                </li>
                                <li class="mb-4">
                                    <Link :href="route('content_hubs.cue_cards')" class="mr-4 hover:underline md:mr-6 ">
                                        IELTS Speaking Cue Cards
                                    </Link>
                                </li>
                                <li class="mb-4">
                                    <Link :href="route('content_hubs.question_topics')"
                                          class="mr-4 hover:underline md:mr-6 ">IELTS Part 1 Questions
                                    </Link>
                                </li>
                                <li class="mb-4">
                                    <Link :href="route('content_hubs.sample_speaking_exams')"
                                          class="mr-4 hover:underline md:mr-6">IELTS Speaking Test Samples
                                    </Link>
                                </li>

                            </ul>
                        </ul>
                    </div>
                    <div>
                        <h2 class="mb-6 text-md font-semibold text-white uppercase dark:text-white">Legal</h2>
                        <ul class="text-white dark:text-gray-400">
                            <li class="mb-4">
                                <Link :href="route('page.terms_of_use')" class="mr-4 hover:underline md:mr-6">Terms of
                                    Use
                                </Link>
                            </li>
                            <li>
                                <Link :href="route('page.privacy_policy')" class="mr-4 hover:underline md:mr-6">Privacy
                                    Policy
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <hr class="my-6 border-gray-800 sm:mx-auto dark:border-gray-700 lg:my-8"/>
            <div class="sm:flex sm:items-center sm:justify-between">
                <div>
                    <Link :href="route('page.terms_of_use')" class="mr-4 hover:underline md:mr-6">Terms of
                        Use
                    </Link>
                    <Link :href="route('page.privacy_policy')" class="mr-4 hover:underline md:mr-6">Privacy
                        Policy
                    </Link>
                </div>
                <div class="text-sm text-gray-100 sm:text-center dark:text-gray-400">© 2024
                    <Link
                        :href="route('home')" class="hover:underline font-semibold">{{ BRAND_NAME }}™
                    </Link>
                    . All Rights Reserved.
                </div>

            </div>
        </div>
    </footer>

    <!--    <footer class="p-4 bg-primary-600 md:p-8 lg:p-10 dark:bg-gray-800">-->
    <!--        <div class="mx-auto max-w-screen-xl text-center">-->
    <!--            <Link :href="route('home')"-->
    <!--                  class="flex justify-center items-center text-2xl font-semibold text-gray-900 dark:text-white">-->
    <!--                <ApplicationLogo class="" variant="white"/>-->
    <!--            </Link>-->
    <!--            <p class="my-6 text-white dark:text-gray-400">{{ BRAND_NAME }} is online platform for english speaking-->
    <!--                practice.-->
    <!--                We provide you a platform where you can practice english speaking with other people free of cost.</p>-->
    <!--            <ul class="flex flex-wrap justify-center items-center mb-8 text-gray-300 dark:text-white">-->
    <!--                <li>-->
    <!--                    <Link :href="route('page.about_us')" class="mr-4 hover:underline md:mr-6 ">About</Link>-->
    <!--                </li>-->
    <!--                <li>-->
    <!--                    <Link :href="route('page.how_it_works')" class="mr-4 hover:underline md:mr-6 ">How It Works</Link>-->
    <!--                </li>-->
    <!--                <li>-->
    <!--                    <Link :href="route('page.terms_of_use')" class="mr-4 hover:underline md:mr-6">Terms of Use</Link>-->
    <!--                </li>-->
    <!--                <li>-->
    <!--                    <Link :href="route('page.privacy_policy')" class="mr-4 hover:underline md:mr-6">Privacy Policy-->
    <!--                    </Link>-->
    <!--                </li>-->
    <!--                <li>-->
    <!--                    <Link :href="route('page.faq')" class="mr-4 hover:underline md:mr-6">FAQs</Link>-->
    <!--                </li>-->
    <!--                <li>-->
    <!--                    <Link :href="route('page.contact_us')" class="mr-4 hover:underline md:mr-6">Contact</Link>-->
    <!--                </li>-->
    <!--            </ul>-->
    <!--            <span class="text-sm text-white sm:text-center dark:text-gray-400">© 2022-2024 <Link-->
    <!--                :href="route('home')"-->
    <!--                class="hover:underline">{{ BRAND_NAME }}™</Link>. All Rights Reserved.</span>-->
    <!--        </div>-->
    <!--    </footer>-->
</template>

<style scoped>

</style>
